import { Search, X, SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  CATEGORY_LABELS, CATEGORY_VALUES,
  type ExpenseCategory, type ExpenseFilters, type ExpensePaymentMethod,
} from "@cold-crew/shared";

interface GastoFiltersBarProps {
  filters:  Partial<ExpenseFilters>;
  onChange: (next: Partial<ExpenseFilters>) => void;
}

const PAYMENT_OPTIONS: { value: ExpensePaymentMethod | ""; label: string }[] = [
  { value: "",              label: "Todo pago"     },
  { value: "efectivo",      label: "Efectivo"      },
  { value: "transferencia", label: "Transferencia" },
  { value: "tarjeta",       label: "Tarjeta"       },
  { value: "cheque",        label: "Cheque"        },
  { value: "otro",          label: "Otro"          },
];

const base = "h-11 w-full rounded-xl border border-[#ddd8cf] bg-white px-3 text-sm text-[#282622] placeholder:text-[#aaa59c] transition-colors focus:border-[#f47b2a] focus:outline-none focus:ring-2 focus:ring-[#f47b2a]/15";

export function GastoFiltersBar({ filters, onChange }: GastoFiltersBarProps) {
  const [open, setOpen] = useState(false);
  const hasActive = !!(filters.search || filters.categoria || filters.metodo_pago
    || filters.date_from || filters.date_to);

  function clear() { onChange({ page: 1, limit: filters.limit ?? 20 }); }

  return (
    <div className="space-y-2">
      {/* Search + toggle */}
      <div className="flex gap-2.5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#8b867e] pointer-events-none" />
          <input type="search" placeholder="Descripción o proveedor…"
            value={filters.search ?? ""}
            onChange={(e) => onChange({ ...filters, search: e.target.value || undefined, page: 1 })}
            className={cn(base, "pl-10")} />
        </div>
       {/* Botón Filtros — activo usa el mismo degradado del Hero/Sidebar */}
        <button onClick={() => setOpen((v) => !v)}
          style={(open || hasActive) ? { background: "#090909" } : undefined}
          className={cn(
            "flex h-11 shrink-0 items-center gap-2 rounded-xl border px-4 text-sm font-semibold transition-colors",
            open || hasActive
              ? "border-[#171614] text-white"
              : "border-[#ddd8cf] bg-white text-[#615d56] hover:bg-[#f8f6f1] hover:text-[#171614]"
          )}>
          <SlidersHorizontal className="size-4" />
          <span className="hidden sm:inline">Filtros</span>
          {hasActive && <span className="size-2 rounded-full bg-[#FF6500]" />}
        </button>
      </div>

      {open && (
        <div className="space-y-3 rounded-2xl border border-[#ded9cf] bg-white p-4">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-[#8b867e] mb-1 block">Categoría</label>
              <select value={filters.categoria ?? ""}
                onChange={(e) => onChange({ ...filters, categoria: (e.target.value as ExpenseCategory) || undefined, page: 1 })}
                className={cn(base, "pr-8 cursor-pointer")}>
                <option value="">Todas</option>
                {CATEGORY_VALUES.map((cat) => (
                  <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-[#8b867e] mb-1 block">Método de pago</label>
              <select value={filters.metodo_pago ?? ""}
                onChange={(e) => onChange({ ...filters, metodo_pago: (e.target.value as ExpensePaymentMethod) || undefined, page: 1 })}
                className={cn(base, "pr-8 cursor-pointer")}>
                {PAYMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-[#8b867e] mb-1 block">Desde</label>
              <input type="date" value={filters.date_from ?? ""}
                onChange={(e) => onChange({ ...filters, date_from: e.target.value || undefined, page: 1 })}
                className={base} />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-[#8b867e] mb-1 block">Hasta</label>
              <input type="date" value={filters.date_to ?? ""}
                onChange={(e) => onChange({ ...filters, date_to: e.target.value || undefined, page: 1 })}
                className={base} />
            </div>
          </div>

          {hasActive && (
            <button onClick={clear}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <X className="size-3" /> Limpiar filtros
            </button>
          )}
        </div>
      )}
    </div>
  );
}
